import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { postizAPI } from "../api/postizAPI";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Both LinkedIn and Facebook providers return analytics as:
 *   Array<{ label: string, data: Array<{ total: string|number, date: string }> }>
 */
const extractMetrics = (analyticsPayload) => {
  const entries = Array.isArray(analyticsPayload)
    ? analyticsPayload
    : Array.isArray(analyticsPayload?.data)
    ? analyticsPayload.data
    : [];

  let likes = 0;
  let comments = 0;
  let shares = 0;
  let impressions = 0;

  for (const entry of entries) {
    const total = (entry?.data || []).reduce(
      (sum, point) => sum + toNumber(point?.total ?? point?.value ?? 0),
      0
    );
    const label = String(entry?.label || "").toLowerCase();

    if (label.includes("like") || label.includes("reaction") || label.includes("favorite")) {
      likes += total;
    } else if (label.includes("comment") || label.includes("reply")) {
      comments += total;
    } else if (label.includes("share") || label.includes("repost") || label.includes("retweet")) {
      shares += total;
    } else if (label.includes("impression") || label.includes("reach") || label.includes("view")) {
      impressions += total;
    }
  }

  return { likes, comments, shares, impressions, engagement: likes + comments + shares };
};

const formatNumber = (num) => {
  const n = toNumber(num);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const formatDate = (date) => {
  if (!date) return "Unknown";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "Unknown";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/** Return the platform accent color for icon backgrounds */
const getPlatformColor = (identifier) => {
  const id = String(identifier || "").toLowerCase();
  if (id.includes("instagram")) return "#E1306C";
  if (id.includes("facebook")) return "#1877F2";
  if (id.includes("linkedin")) return "#0A66C2";
  if (id.includes("twitter") || id === "x") return "#000000";
  if (id.includes("tiktok")) return "#010101";
  if (id.includes("youtube")) return "#FF0000";
  if (id.includes("pinterest")) return "#E60023";
  if (id.includes("threads")) return "#101010";
  return "#612bd3";
};

/** Font Awesome class for platform icon */
const getPlatformIcon = (identifier) => {
  const id = String(identifier || "").toLowerCase();
  if (id.includes("instagram")) return "fa-brands fa-instagram";
  if (id.includes("facebook")) return "fa-brands fa-facebook-f";
  if (id.includes("linkedin")) return "fa-brands fa-linkedin-in";
  if (id.includes("twitter") || id === "x") return "fa-brands fa-x-twitter";
  if (id.includes("tiktok")) return "fa-brands fa-tiktok";
  if (id.includes("youtube")) return "fa-brands fa-youtube";
  if (id.includes("pinterest")) return "fa-brands fa-pinterest-p";
  if (id.includes("threads")) return "fa-brands fa-threads";
  return "fa-solid fa-share-nodes";
};

/** ── Shared media carousel for post previews ────────────────────── */

const navBtnStyle = (side) => ({
  position: "absolute",
  top: "50%",
  [side]: 6,
  transform: "translateY(-50%)",
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: "rgba(0,0,0,0.45)",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontSize: 18,
  lineHeight: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
});

/**
 * Renders post media as a carousel when multiple images are present.
 * - aspectRatio: e.g. "1" for Instagram square crops. Omit for natural sizing.
 * - maxHeight: optional cap on image height when no aspectRatio is set.
 */
const MediaCarousel = ({ images, isDarkMode, maxHeight, aspectRatio }) => {
  const [index, setIndex] = useState(0);

  const urls = (images || []).map((img) => img.url || img.path).filter(Boolean);
  if (urls.length === 0) return null;

  const safeIndex = Math.min(index, urls.length - 1);
  const dotColor = isDarkMode ? "#555" : "#ccc";
  const dotActiveColor = isDarkMode ? "#fff" : "#000";

  return (
    <div style={{ position: "relative", width: "100%", overflow: "hidden", ...(aspectRatio ? { aspectRatio } : {}) }}>
      <img
        src={urls[safeIndex]}
        alt=""
        style={{
          width: "100%",
          display: "block",
          ...(aspectRatio
            ? { height: "100%", objectFit: "cover" }
            : { ...(maxHeight ? { maxHeight } : {}), objectFit: "cover" }),
        }}
      />
      {urls.length > 1 && (
        <>
          {safeIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setIndex((i) => Math.max(0, i - 1)); }}
              style={navBtnStyle("left")}
              aria-label="Previous image"
            >
              ‹
            </button>
          )}
          {safeIndex < urls.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setIndex((i) => Math.min(urls.length - 1, i + 1)); }}
              style={navBtnStyle("right")}
              aria-label="Next image"
            >
              ›
            </button>
          )}
          <div
            style={{
              position: "absolute",
              bottom: 8,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 5,
              zIndex: 2,
            }}
          >
            {urls.map((_, i) => (
              <div
                key={i}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: i === safeIndex ? dotActiveColor : dotColor,
                  opacity: i === safeIndex ? 1 : 0.6,
                }}
              />
            ))}
          </div>
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              fontSize: 11,
              padding: "2px 7px",
              borderRadius: 10,
              zIndex: 2,
            }}
          >
            {safeIndex + 1}/{urls.length}
          </div>
        </>
      )}
    </div>
  );
};

/** ── Social-native post card previews ──────────────────────────── */

const TwitterPreview = ({ post, isDarkMode }) => {
  const bg = isDarkMode ? "#15202b" : "#ffffff";
  const border = isDarkMode ? "#2f3336" : "#e7e9ea";
  const text = isDarkMode ? "#e7e9ea" : "#0f1419";
  const sub = isDarkMode ? "#71767b" : "#536471";
  const hasMedia = (post.images || []).length > 0;

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16, padding: "16px", fontFamily: "'TwitterChirp', -apple-system, sans-serif", color: text }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#612bd3", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {post.integration?.picture
            ? <img src={post.integration.picture} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
            : <i className="fa-brands fa-x-twitter" style={{ color: "#fff", fontSize: 16 }} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {post.integration?.name || "Account"}
          </div>
          <div style={{ color: sub, fontSize: 13 }}>@{(post.integration?.name || "account").toLowerCase().replace(/\s+/g, "")} · {formatDate(post.publishDate)}</div>
        </div>
        <i className="fa-brands fa-x-twitter" style={{ color: sub, fontSize: 18 }} />
      </div>
      {/* Content */}
      <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: hasMedia ? 10 : 0, wordBreak: "break-word" }}>
        {post.content || "(No content)"}
      </div>
      {/* Media */}
      {hasMedia && (
        <div style={{ borderRadius: 12, marginBottom: 10 }}>
          <MediaCarousel images={post.images} isDarkMode={isDarkMode} />
        </div>
      )}
      {/* Actions */}
      <div style={{ display: "flex", gap: 28, color: sub, fontSize: 13, marginTop: 8 }}>
        <span><i className="fa-regular fa-comment" style={{ marginRight: 4 }} />{formatNumber(post.comments)}</span>
        <span><i className="fa-solid fa-retweet" style={{ marginRight: 4 }} />{formatNumber(post.shares)}</span>
        <span><i className="fa-regular fa-heart" style={{ marginRight: 4 }} />{formatNumber(post.likes)}</span>
      </div>
    </div>
  );
};

const InstagramPreview = ({ post, isDarkMode }) => {
  const bg = isDarkMode ? "#1c1c1e" : "#ffffff";
  const border = isDarkMode ? "#2c2c2e" : "#dbdbdb";
  const text = isDarkMode ? "#ffffff" : "#000000";
  const sub = isDarkMode ? "#a1a1a1" : "#8e8e8e";
  const hasMedia = (post.images || []).length > 0;

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 12, fontFamily: "-apple-system, 'Segoe UI', sans-serif", color: text, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)", padding: 2, flexShrink: 0 }}>
          <div style={{ borderRadius: "50%", overflow: "hidden", width: "100%", height: "100%", background: bg }}>
            {post.integration?.picture
              ? <img src={post.integration.picture} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ width: "100%", height: "100%", background: "#612bd3", display: "flex", alignItems: "center", justifyContent: "center" }}><i className="fa-brands fa-instagram" style={{ color: "#fff", fontSize: 14 }} /></div>}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{post.integration?.name || "account"}</div>
          <div style={{ color: sub, fontSize: 11 }}>{formatDate(post.publishDate)}</div>
        </div>
        <i className="fa-brands fa-instagram" style={{ fontSize: 20, background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }} />
      </div>
      {/* Media */}
      {hasMedia ? (
        <MediaCarousel images={post.images} isDarkMode={isDarkMode} aspectRatio="1" />
      ) : (
        <div style={{ width: "100%", aspectRatio: "1", background: isDarkMode ? "#2c2c2e" : "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className="fa-regular fa-image" style={{ fontSize: 32, color: sub }} />
        </div>
      )}
      {/* Actions */}
      <div style={{ padding: "10px 12px 4px" }}>
        <div style={{ display: "flex", gap: 14, marginBottom: 6, color: text, fontSize: 20 }}>
          <i className="fa-regular fa-heart" style={{ cursor: "pointer" }} />
          <i className="fa-regular fa-comment" style={{ cursor: "pointer" }} />
          <i className="fa-regular fa-paper-plane" style={{ cursor: "pointer" }} />
        </div>
        <div style={{ fontSize: 12, fontWeight: 600 }}>{formatNumber(post.likes)} likes</div>
        <div style={{ fontSize: 12, color: text, marginTop: 3 }}>
          <span style={{ fontWeight: 600 }}>{post.integration?.name || "account"} </span>
          {post.content || ""}
        </div>
        {toNumber(post.comments) > 0 && (
          <div style={{ fontSize: 11, color: sub, marginTop: 2 }}>View all {formatNumber(post.comments)} comments</div>
        )}
      </div>
    </div>
  );
};

const LinkedInPreview = ({ post, isDarkMode }) => {
  const bg = isDarkMode ? "#1b1f23" : "#ffffff";
  const border = isDarkMode ? "#38434f" : "#e0e0e0";
  const text = isDarkMode ? "#e7e3dc" : "#000000";
  const sub = isDarkMode ? "#a0aab4" : "#666666";
  const hasMedia = (post.images || []).length > 0;

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, fontFamily: "'Segoe UI', -apple-system, sans-serif", color: text, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px 8px" }}>
        <div style={{ width: 40, height: 40, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: "#0A66C2", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {post.integration?.picture
            ? <img src={post.integration.picture} alt="" style={{ width: 40, height: 40, objectFit: "cover" }} />
            : <i className="fa-brands fa-linkedin-in" style={{ color: "#fff", fontSize: 18 }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{post.integration?.name || "Account"}</div>
          <div style={{ color: sub, fontSize: 11 }}>{formatDate(post.publishDate)}</div>
        </div>
        <i className="fa-brands fa-linkedin" style={{ color: "#0A66C2", fontSize: 20 }} />
      </div>
      {/* Content */}
      <div style={{ padding: "0 16px 8px", fontSize: 13, lineHeight: 1.5 }}>
        {post.content || "(No content)"}
      </div>
      {/* Media */}
      {hasMedia && <MediaCarousel images={post.images} isDarkMode={isDarkMode} />}
      {/* Actions */}
      <div style={{ padding: "8px 16px", display: "flex", gap: 16, borderTop: `1px solid ${border}`, color: sub, fontSize: 12 }}>
        <span><i className="fa-regular fa-thumbs-up" style={{ marginRight: 4 }} />{formatNumber(post.likes)}</span>
        <span><i className="fa-regular fa-comment" style={{ marginRight: 4 }} />{formatNumber(post.comments)}</span>
        <span><i className="fa-solid fa-share" style={{ marginRight: 4 }} />{formatNumber(post.shares)}</span>
      </div>
    </div>
  );
};

const FacebookPreview = ({ post, isDarkMode }) => {
  const bg = isDarkMode ? "#242526" : "#ffffff";
  const border = isDarkMode ? "#3a3b3c" : "#dddfe2";
  const text = isDarkMode ? "#e4e6ea" : "#050505";
  const sub = isDarkMode ? "#b0b3b8" : "#65676b";
  const hasMedia = (post.images || []).length > 0;

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, fontFamily: "Helvetica, Arial, sans-serif", color: text, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px 8px" }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#1877F2", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {post.integration?.picture
            ? <img src={post.integration.picture} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "50%" }} />
            : <i className="fa-brands fa-facebook-f" style={{ color: "#fff", fontSize: 18 }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{post.integration?.name || "Page"}</div>
          <div style={{ color: sub, fontSize: 12 }}>{formatDate(post.publishDate)}</div>
        </div>
        <i className="fa-brands fa-facebook" style={{ color: "#1877F2", fontSize: 20 }} />
      </div>
      <div style={{ padding: "0 16px 8px", fontSize: 14, lineHeight: 1.5 }}>
        {post.content || "(No content)"}
      </div>
      {hasMedia && <MediaCarousel images={post.images} isDarkMode={isDarkMode} />}
      <div style={{ padding: "8px 16px", display: "flex", gap: 4, borderTop: `1px solid ${border}`, color: sub, fontSize: 13 }}>
        <button style={{ flex: 1, background: "none", border: "none", color: sub, cursor: "pointer", padding: "6px 0", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          <i className="fa-regular fa-thumbs-up" />{formatNumber(post.likes)} Like
        </button>
        <button style={{ flex: 1, background: "none", border: "none", color: sub, cursor: "pointer", padding: "6px 0", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          <i className="fa-regular fa-comment" />{formatNumber(post.comments)} Comment
        </button>
        <button style={{ flex: 1, background: "none", border: "none", color: sub, cursor: "pointer", padding: "6px 0", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          <i className="fa-solid fa-share" />{formatNumber(post.shares)} Share
        </button>
      </div>
    </div>
  );
};

const GenericPreview = ({ post, isDarkMode }) => {
  const identifier = post.integration?.providerIdentifier || "unknown";
  const color = getPlatformColor(identifier);
  const icon = getPlatformIcon(identifier);
  const bg = isDarkMode ? "#1a1f2e" : "#ffffff";
  const border = isDarkMode ? "#2d3748" : "#e2e8f0";
  const text = isDarkMode ? "#e2e8f0" : "#1a202c";
  const sub = isDarkMode ? "#718096" : "#718096";
  const hasMedia = (post.images || []).length > 0;

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 12, overflow: "hidden", fontFamily: "-apple-system, sans-serif", color: text }}>
      {/* Platform banner */}
      <div style={{ background: color, padding: "8px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {post.integration?.picture
            ? <img src={post.integration.picture} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
            : <i className={icon} style={{ color: "#fff", fontSize: 16 }} />}
          <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>{post.integration?.name || "Account"}</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>{formatDate(post.publishDate)}</span>
      </div>
      {hasMedia && <MediaCarousel images={post.images} isDarkMode={isDarkMode} />}
      <div style={{ padding: "12px 14px" }}>
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          {post.content || "(No content)"}
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 14, color: sub, fontSize: 12 }}>
          <span><i className="fa-regular fa-heart" style={{ marginRight: 4 }} />{formatNumber(post.likes)}</span>
          <span><i className="fa-regular fa-comment" style={{ marginRight: 4 }} />{formatNumber(post.comments)}</span>
          <span><i className="fa-solid fa-retweet" style={{ marginRight: 4 }} />{formatNumber(post.shares)}</span>
        </div>
      </div>
    </div>
  );
};

/** Render the correct card for each platform */
const PostPreviewCard = ({ post, isDarkMode }) => {
  const id = String(post.integration?.providerIdentifier || "").toLowerCase();
  if (id === "x" || id.includes("twitter")) return <TwitterPreview post={post} isDarkMode={isDarkMode} />;
  if (id.includes("instagram")) return <InstagramPreview post={post} isDarkMode={isDarkMode} />;
  if (id.includes("linkedin")) return <LinkedInPreview post={post} isDarkMode={isDarkMode} />;
  if (id.includes("facebook")) return <FacebookPreview post={post} isDarkMode={isDarkMode} />;
  return <GenericPreview post={post} isDarkMode={isDarkMode} />;
};

/* ── Main component ─────────────────────────────────────────────── */

export default function SharedPostsAnalytics({ token, isDarkMode = false }) {
  const [dateRange, setDateRange] = useState("30");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postsWithMetrics, setPostsWithMetrics] = useState([]);

  // Dark-mode palette (matches existing app)
  const c = {
    pageBg:       isDarkMode ? "transparent"  : "transparent",
    cardBg:       isDarkMode ? "#111827"       : "#ffffff",
    cardBorder:   isDarkMode ? "#1f2937"       : "#e5e7eb",
    text:         isDarkMode ? "#f9fafb"       : "#111827",
    sub:          isDarkMode ? "#9ca3af"       : "#6b7280",
    accent:       "#612bd3",
    accentHover:  "#5023b8",
    rangeBg:      isDarkMode ? "#1f2937"       : "#f3f4f6",
    rangeActive:  "#612bd3",
  };

  useEffect(() => {
    if (token) loadAnalytics();
  }, [token, dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const days = parseInt(dateRange, 10);
      const since = new Date();
      since.setDate(since.getDate() - days);
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 1);

      const postsResult = await postizAPI.getPostsByDateRange(token, {
        startDate: since.toISOString(),
        endDate: endDate.toISOString(),
      });
      if (!postsResult.success) throw new Error(postsResult.error || "Failed to fetch shared posts");

      const publishedPosts = (postsResult.data?.posts || [])
        .filter((p) => String(p.state || "").toUpperCase() === "PUBLISHED")
        .filter((p) => { if (!p.publishDate) return false; const d = new Date(p.publishDate); return !Number.isNaN(d.getTime()) && d >= since; })
        .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));

      const analyticsRows = await Promise.all(
        publishedPosts.slice(0, 30).map(async (post) => {
          const [analyticsRes, groupRes] = await Promise.all([
            postizAPI.getPostAnalytics(token, post.id, days),
            postizAPI.getPostGroup(token, post.group || post.id),
          ]);
          const metrics = analyticsRes.success
            ? extractMetrics(analyticsRes.data)
            : { likes: 0, comments: 0, shares: 0, impressions: 0, engagement: 0 };

          // Full image array (supports carousels) from the post group endpoint.
          const images = groupRes.success ? (groupRes.data?.images || []) : [];

          return { ...post, ...metrics, images };
        })
      );
      setPostsWithMetrics(analyticsRows);
    } catch (err) {
      console.error("Error loading shared posts analytics:", err);
      setError(err.message || "Failed to load");
      toast.error("Failed to load shared posts analytics");
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    const likes = postsWithMetrics.reduce((s, p) => s + toNumber(p.likes), 0);
    const comments = postsWithMetrics.reduce((s, p) => s + toNumber(p.comments), 0);
    const shares = postsWithMetrics.reduce((s, p) => s + toNumber(p.shares), 0);
    const impressions = postsWithMetrics.reduce((s, p) => s + toNumber(p.impressions), 0);
    return { totalPosts: postsWithMetrics.length, likes, comments, shares, impressions, engagement: likes + comments + shares };
  }, [postsWithMetrics]);

  const platformMetrics = useMemo(() => {
    const map = new Map();
    postsWithMetrics.forEach((post) => {
      const platform = post.integration?.providerIdentifier || post.integration?.name || "unknown";
      const cur = map.get(platform) || { platform, posts: 0, likes: 0, comments: 0, shares: 0, impressions: 0 };
      cur.posts += 1; cur.likes += toNumber(post.likes); cur.comments += toNumber(post.comments);
      cur.shares += toNumber(post.shares); cur.impressions += toNumber(post.impressions);
      map.set(platform, cur);
    });
    return Array.from(map.values()).map((r) => ({ ...r, engagement: r.likes + r.comments + r.shares })).sort((a, b) => b.engagement - a.engagement);
  }, [postsWithMetrics]);

  const trendRows = useMemo(() => {
    const pts = new Map();
    postsWithMetrics.forEach((post) => {
      if (!post.publishDate) return;
      const d = new Date(post.publishDate);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toISOString().split("T")[0];
      const cur = pts.get(key) || { date: key, likes: 0, comments: 0, shares: 0, impressions: 0 };
      cur.likes += toNumber(post.likes); cur.comments += toNumber(post.comments);
      cur.shares += toNumber(post.shares); cur.impressions += toNumber(post.impressions);
      pts.set(key, cur);
    });
    return Array.from(pts.values()).map((r) => ({ ...r, engagement: r.likes + r.comments + r.shares })).sort((a, b) => a.date > b.date ? 1 : -1);
  }, [postsWithMetrics]);

  const maxTrend = Math.max(...trendRows.map((r) => r.engagement), 0);

  /* ── Spinner ── */
  if (loading) {
    return (
      <div style={{ padding: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 40, height: 40, border: `4px solid ${c.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const recentFour = postsWithMetrics.slice(0, 4);

  /* ── Shared card wrapper ── */
  const Card = ({ children, style = {} }) => (
    <div style={{ background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: 12, padding: "20px 22px", ...style }}>
      {children}
    </div>
  );

  const SectionTitle = ({ children }) => (
    <h3 style={{ fontSize: 16, fontWeight: 700, color: c.text, marginBottom: 16, letterSpacing: "-0.01em" }}>
      {children}
    </h3>
  );

  return (
    <div style={{ padding: "15px 20px", color: c.text, fontFamily: "Inter, -apple-system, sans-serif" }}>
      {/* Font Awesome */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css" />

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: c.text, margin: 0, letterSpacing: "-0.02em" }}>
            Shared Posts Analytics
          </h2>
          <p style={{ fontSize: 13, color: c.sub, marginTop: 3 }}>
            Engagement metrics for your published social posts
          </p>
        </div>
        {/* Date range selector */}
        <div style={{ display: "flex", gap: 6, background: c.rangeBg, borderRadius: 10, padding: 4 }}>
          {[{ label: "7 days", value: "7" }, { label: "30 days", value: "30" }, { label: "90 days", value: "90" }].map((r) => (
            <button
              key={r.value}
              onClick={() => setDateRange(r.value)}
              style={{
                padding: "6px 14px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                background: dateRange === r.value ? c.accent : "transparent",
                color: dateRange === r.value ? "#ffffff" : c.sub,
                transition: "background 0.15s",
                fontFamily: "inherit",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ── Summary stat cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Published Posts", value: summary.totalPosts, icon: "fa-solid fa-paper-plane" },
          { label: "Likes", value: formatNumber(summary.likes), icon: "fa-regular fa-heart" },
          { label: "Comments", value: formatNumber(summary.comments), icon: "fa-regular fa-comment" },
          { label: "Shares", value: formatNumber(summary.shares), icon: "fa-solid fa-share-nodes" },
          { label: "Impressions", value: formatNumber(summary.impressions), icon: "fa-regular fa-eye" },
        ].map((card) => (
          <div key={card.label} style={{ background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <i className={card.icon} style={{ color: c.accent, fontSize: 12 }} />
              <span style={{ fontSize: 11, fontWeight: 500, color: c.sub, textTransform: "uppercase", letterSpacing: "0.06em" }}>{card.label}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: c.text, letterSpacing: "-0.02em" }}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* ── Latest Shared Posts – 2×2 grid ── */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <SectionTitle>Latest Shared Posts</SectionTitle>
          {postsWithMetrics.length === 0 && (
            <span style={{ fontSize: 12, color: c.sub }}>No published posts in this range</span>
          )}
        </div>

        {postsWithMetrics.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: c.sub, fontSize: 14 }}>
            <i className="fa-regular fa-folder-open" style={{ fontSize: 32, marginBottom: 12, display: "block" }} />
            No published posts found for this date range.
          </div>
        ) : (
          /* Scrollable container with 2×2 grid (shows 4 posts, scrollable for more) */
          <div style={{ maxHeight: 720, overflowY: "auto", paddingRight: 4 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              {postsWithMetrics.slice(0, Math.max(4, postsWithMetrics.length)).slice(0, 4).map((post) => (
                <div key={post.id} style={{ minWidth: 0 }}>
                  <PostPreviewCard post={post} isDarkMode={isDarkMode} />
                  {post.releaseURL && (
                    <a
                      href={post.releaseURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 11, color: c.accent, textDecoration: "none" }}
                    >
                      <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: 10 }} />
                      View live post
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* ── Engagement Over Time ── */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>Engagement Over Time</SectionTitle>
        {trendRows.length === 0 ? (
          <p style={{ color: c.sub, fontSize: 14 }}>No published post analytics found for this date range.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {trendRows.map((row) => {
              const widthPct = maxTrend > 0 ? Math.max(4, (row.engagement / maxTrend) * 100) : 4;
              return (
                <div key={row.date}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5 }}>
                    <span style={{ color: c.text }}>{formatDate(row.date)}</span>
                    <span style={{ color: c.sub }}>{formatNumber(row.engagement)} engagement</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: isDarkMode ? "#1f2937" : "#e5e7eb" }}>
                    <div style={{ height: 8, borderRadius: 4, width: `${widthPct}%`, background: `linear-gradient(90deg, ${c.accent}, #8b5cf6)`, transition: "width 0.4s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Platform Breakdown ── */}
      <Card>
        <SectionTitle>Platform Breakdown</SectionTitle>
        {platformMetrics.length === 0 ? (
          <p style={{ color: c.sub, fontSize: 14 }}>No platform metrics available yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ color: c.sub }}>
                  {["Platform", "Posts", "Likes", "Comments", "Shares", "Engagement"].map((h, i) => (
                    <th key={h} style={{ textAlign: i === 0 ? "left" : "right", padding: "6px 8px", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {platformMetrics.map((row) => (
                  <tr key={row.platform} style={{ borderTop: `1px solid ${c.cardBorder}` }}>
                    <td style={{ padding: "10px 8px", color: c.text }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, background: getPlatformColor(row.platform), display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className={getPlatformIcon(row.platform)} style={{ color: "#fff", fontSize: 11 }} />
                        </div>
                        <span style={{ textTransform: "capitalize" }}>{row.platform}</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "right", color: c.sub }}>{row.posts}</td>
                    <td style={{ padding: "10px 8px", textAlign: "right", color: c.sub }}>{formatNumber(row.likes)}</td>
                    <td style={{ padding: "10px 8px", textAlign: "right", color: c.sub }}>{formatNumber(row.comments)}</td>
                    <td style={{ padding: "10px 8px", textAlign: "right", color: c.sub }}>{formatNumber(row.shares)}</td>
                    <td style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: c.accent }}>{formatNumber(row.engagement)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}